import React, { useCallback, useEffect, useRef, useState } from 'react';
import { kindSegments, kindLabel, kindIsPackOnly } from '../../lib/phaseKinds';
import { processImageForUpload } from '../../lib/imageUtils';
import { supabase } from '../../lib/supabase';
import { subscribe as subscribeQueue, getSnapshot as getQueueSnapshot } from '../../lib/portalQueue';
import { Button } from '../ui';

// ---------------------------------------------------------------------------
// Crew-portal shared helpers — pure functions, the photo-capture hook +
// field, and the small feedback pieces the tabs and screens share. Split
// out of CrewPortalPage.jsx in the 2026-09-15 phone-shell rebuild
// (BBM Context for claude/mobile.md). Nothing here renders a screen.
// ---------------------------------------------------------------------------

export function formatTime12(t) {
    if (!t) return '';
    const [h, m] = String(t).split(':').map(Number);
    if (Number.isNaN(h)) return t;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}

// "Mon, Sep 14" for header sublines.
export function formatDateShort(ymd) {
    if (!ymd) return '';
    return new Date(ymd + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Token resolution used to be URL → localStorage (PIN era). Email/password
// is the only auth path now (legacy token+PIN retired 2026-06-24); we keep
// this one helper to scrub any stale PIN-era token from localStorage on
// sign-in/out so it can't linger.
const TOKEN_STORAGE_KEY = 'bbm_crew_token';
export function clearPersistedToken() {
    try { window.localStorage.removeItem(TOKEN_STORAGE_KEY); } catch {}
}

// Auth header for the JWT path (email + password sign-in,
// crew-auth-plan-v1). Empty object when there's no Supabase session —
// callers spread it into fetch headers unconditionally.
export async function getPortalAuthHeaders() {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) return { Authorization: `Bearer ${session.access_token}` };
    } catch {}
    return {};
}

// Photo group key — primary room. Photos may carry multiple tags
// (room_tags text[]); first entry wins. Legacy single-string room_tag
// is the fallback. Untagged photos land in "General".
export const primaryRoom = (p) => (
    Array.isArray(p.room_tags) && p.room_tags.length
        ? p.room_tags[0]
        : (p.room_tag || 'General')
);

// HH:MM → minutes since midnight (null-safe).
export function timeToMin(hhmm) {
    if (!hhmm || typeof hhmm !== 'string') return NaN;
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
    return h * 60 + m;
}

// Encode a File as raw base64 (no data:URI prefix) for JSON photo uploads.
export function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^;]+;base64,/, ''));
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(file);
    });
}

// Paid hours = clock span minus break minutes (null if either side blank).
// Mirrors computeDayHours in src/lib/database.js + the server copy.
export function computePaidHours(clockIn, clockOut, breakMinutes = 0) {
    const s = timeToMin(clockIn), e = timeToMin(clockOut);
    if (Number.isNaN(s) || Number.isNaN(e)) return null;
    const total = e - s - (Number(breakMinutes) || 0);
    if (total <= 0) return 0;
    return Math.round((total / 60) * 100) / 100;
}

// Shared HH:MM → hours math for the billing pair. Lunch is unpaid time.
export function hoursBetween(s, e, lunchMinutes) {
    if (!s || !e) return null;
    const [sh, sm] = s.split(':').map(Number);
    const [eh, em] = e.split(':').map(Number);
    if ([sh, sm, eh, em].some(Number.isNaN)) return null;
    const totalMin = (eh * 60 + em) - (sh * 60 + sm) - (Number(lunchMinutes) || 0);
    if (totalMin <= 0) return 0;
    return Math.round((totalMin / 60) * 100) / 100;
}

// Billable = not a walkthrough, not cancelled.
export const billableOf = (jobs) => (jobs || []).filter(j => j.type !== 'WALKTHROUGH' && j.status !== 'cancelled');

// Does the day actually involve a BBM truck? Pure pack days (the PACKING
// type or a pack-phase leg of a move) and labor-only days don't — no truck
// got loaded, so no reset photo to demand (Jaylen 2026-08-07). pack_load
// counts as truck-using: the load half fills a truck.
export const TRUCK_RESET_TYPES = new Set(['LOCAL', 'LONG_DISTANCE', 'DELIVERY', 'PICKUP']);
export const dayUsesTruck = (billable) => billable.some(j => TRUCK_RESET_TYPES.has(j.type) && !kindIsPackOnly(j.phase_kind));

// 90-min post-submit edit window — the lead can still correct a
// just-submitted recap (Jaylen) before it settles into read-only.
export const RECAP_EDIT_WINDOW_MS = 90 * 60 * 1000;
export const withinRecapEditWindow = (job) =>
    !!job.recap_submitted_at && (Date.now() - new Date(job.recap_submitted_at).getTime() < RECAP_EDIT_WINDOW_MS);

// Job-card status pill (mobile.md Jobs tab): On site / Up next / Done /
// Cancelled. There is no live clock (Crew portal 2 pick A), so "On site"
// is "the job's start time has passed today and it isn't done yet".
export function jobStatusPill(job, validDate) {
    if (job.status === 'cancelled') return { label: 'Cancelled', color: '#94a3b8' };
    if (job.status === 'completed' || job.recap_submitted_at) return { label: 'Done', color: '#059669' };
    const today = new Date();
    const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (validDate === todayYmd) {
        const nowMin = today.getHours() * 60 + today.getMinutes();
        const startMin = timeToMin(job.actual_start_time || job.time);
        if (!Number.isNaN(startMin) && nowMin >= startMin) return { label: 'On site', color: '#d97706' };
    }
    return { label: 'Up next', color: '#3b82f6' };
}

// Route stops (2026-07-09, Jaylen — Cameron walkthrough): multi-stop jobs
// were navigating to ONLY the first address. Stops can live on the
// ESTIMATE (per-move — where the estimate funnel writes them) or on the
// CLIENT (intake). The estimate's are job-specific, so they win.
// after_destination stops (2026-08-26) come AFTER the main unload in
// drive order — e.g. "drop the sofa at grandma's".
export function routeStopsFor(job, clients, allEstimates) {
    const clientRec = (clients || []).find(c => c.id === job.client_id);
    const estRec = job.estimate_id ? (allEstimates || []).find(e => e.id === job.estimate_id) : null;
    const raw = estRec?.additional_stops || clientRec?.additional_stops;
    if (!raw) return { middleStops: [], afterStops: [] };
    try {
        const a = JSON.parse(raw);
        if (Array.isArray(a)) {
            const entries = a.map(s => ({ address: (s?.address || '').trim(), after: !!s?.after_destination })).filter(s => s.address);
            return {
                middleStops: entries.filter(s => !s.after).map(s => s.address),
                afterStops: entries.filter(s => s.after).map(s => s.address),
            };
        }
    } catch { /* legacy raw string below */ }
    return { middleStops: String(raw).trim() ? [String(raw).trim()] : [], afterStops: [] };
}

// Full-trip directions: current location → pickup → stops → destination →
// any after-unload drops. The nav's final point is the LAST drive point.
export function mapsUrlFor(job, middleStops, afterStops) {
    if (!job.from_address) return '';
    const lastDrop = afterStops.length > 0 ? afterStops[afterStops.length - 1] : null;
    const dest = lastDrop || job.to_address || job.from_address;
    const waypoints = job.to_address
        ? [job.from_address, ...middleStops, ...(lastDrop ? [job.to_address, ...afterStops.slice(0, -1)] : [])]
        : [...middleStops, ...afterStops.slice(0, -1)];
    let url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
    if (waypoints.length > 0) url += `&waypoints=${encodeURIComponent(waypoints.join('|'))}`;
    return url;
}

// cityStateFromAddress — pull "City, ST" out of a US-style street address.
export function cityStateFromAddress(addr) {
    if (!addr || typeof addr !== 'string') return '';
    const m = addr.match(/,\s*([^,]+),\s*([A-Z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/);
    if (m) return `${m[1].trim()}, ${m[2]}`;
    return addr;
}

// Combined label for a day carrying MULTIPLE legs of one trip (Jaylen
// 2026-08-10 — pack + load on the same date): "Pack + Load day".
export function combinedLegLabel(kinds, tripDestination) {
    if (!kinds || kinds.length <= 1) return legLabel(kinds && kinds[0], tripDestination);
    const parts = [...new Set(kinds.map(k => kindLabel(k)))].filter(Boolean);
    return parts.join(' + ') + ' day';
}

export function legLabel(phaseKind, tripDestination) {
    if (phaseKind && kindSegments(phaseKind).length > 1) {
        return kindLabel(phaseKind).split('/').join(' + ') + ' day';
    }
    switch (phaseKind) {
        case 'pack':   return 'Pack day';
        case 'load':   return 'Load day';
        case 'drive': {
            const cs = cityStateFromAddress(tripDestination);
            return cs ? `Drive — headed to ${cs}` : 'Drive day';
        }
        case 'unload': return 'Unload day';
        case 'return': return 'Return — back to BBM';
        case 'hold':   return 'Hold day';
        case 'move':   return 'Move day';
        case 'trash_removal': return 'Trash removal';
        default:
            if (phaseKind && typeof phaseKind === 'string') {
                return phaseKind.charAt(0).toUpperCase() + phaseKind.slice(1);
            }
            return 'Today';
    }
}

// recapSectionsFor — which recap sections apply to a job type. The recap
// surface is shared across LOCAL / LONG_DISTANCE / PACKING / LABOR_ONLY /
// DELIVERY jobs but the fields that make sense vary (a PACKING job has no
// truck to photograph; a labor-only or delivery job has no BOL).
//   times, hours_summary, rating, crew_notes, damage_notes, tip, payment
//   reset_truck_photo — truck-using types (lives on the End of day tab)
//   bol_photo         — LOCAL + LONG_DISTANCE
//   materials         — everything but LABOR_ONLY
//   packing_billing   — PACKING only
// (Stage 5 fuel + odometer was built and pulled from V1, 2026-05-13 —
// the API + schema plumbing stays; re-adding is just the section here.)
export function recapSectionsFor(jobType, phaseKind) {
    const all = new Set(['times', 'hours_summary', 'rating', 'crew_notes', 'damage_notes', 'tip', 'payment']);
    const isPurePackDay = phaseKind === 'pack';
    if (!isPurePackDay && (jobType === 'LOCAL' || jobType === 'LONG_DISTANCE' || jobType === 'DELIVERY' || jobType === 'PICKUP')) {
        all.add('reset_truck_photo');
    }
    if (jobType === 'LOCAL' || jobType === 'LONG_DISTANCE') all.add('bol_photo');
    if (jobType === 'LOCAL' || jobType === 'LONG_DISTANCE' || jobType === 'PACKING' || jobType === 'DELIVERY' || jobType === 'PICKUP') {
        all.add('materials');
    }
    if (jobType === 'PACKING') all.add('packing_billing');
    return all;
}

// Subscribe a component to the offline write-queue snapshot.
export function usePortalQueue() {
    const [items, setItems] = useState(getQueueSnapshot());
    useEffect(() => subscribeQueue(setItems), []);
    return items;
}

// Map an enqueueAndSync outcome onto the office toasts (feedback.md 1B).
// Returns true when the write was QUEUED (offline / transient server
// error) so the caller can show the one allowed inline banner instead.
export function notifyOutcome(show, outcome, error, syncedMsg) {
    if (outcome === 'synced') { show(syncedMsg || 'Saved', 'success'); return false; }
    if (outcome === 'failed') { show(error || 'Save failed', 'error'); return false; }
    return true;
}

// The ONE inline banner the phone apps allow (mobile.md): amber, for a
// write that's sitting in the offline queue.
export function OfflineBanner({ what = 'changes', className = '' }) {
    return (
        <div role="status" className={`rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2.5 text-[13px] font-semibold leading-snug ${className}`}>
            No signal — {what} queued, will send when you're back online
        </div>
    );
}

// Label · value row for review / checklist cards.
export function KV({ label, value, mono = false, className = '' }) {
    return (
        <div className={`flex items-baseline justify-between gap-3 py-1.5 text-[13px] ${className}`}>
            <span className="text-slate-500 flex-shrink-0">{label}</span>
            <span className={`text-slate-800 font-semibold text-right min-w-0 break-words ${mono ? 'font-mono tabular-nums' : ''}`}>{value === null || value === undefined || value === '' ? '—' : value}</span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// usePhotoCapture — owns one photo-capture slot: the picked File + preview
// object URL, the in-flight flag while HEIC→JPEG + downscale runs, and a
// hidden file-input ref. Object URLs are revoked on replace/unmount.
// `onError(msg)` fires when processing itself fails.
// ---------------------------------------------------------------------------
export function usePhotoCapture(onError) {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        return () => { if (preview) URL.revokeObjectURL(preview); };
    }, [preview]);

    const pick = useCallback(async (rawFile) => {
        if (!rawFile) return;
        setProcessing(true);
        try {
            const processed = await processImageForUpload(rawFile);
            const previewUrl = URL.createObjectURL(processed);
            setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return previewUrl; });
            setFile(processed);
        } catch (err) {
            console.warn('photo processing failed:', err);
            if (typeof onError === 'function') onError('Photo processing failed — try a different photo.');
        } finally {
            setProcessing(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    }, [onError]);

    const clear = useCallback(() => {
        setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
        setFile(null);
    }, []);

    return { file, preview, processing, inputRef, pick, clear };
}

// ---------------------------------------------------------------------------
// PhotoCaptureField — one photo slot. Three states: locally selected
// (thumbnail + Replace / Remove), existing on file (thumbnail + ON FILE +
// Re-take), empty (dashed prompt). `capture` is a usePhotoCapture() value.
// ---------------------------------------------------------------------------
export function PhotoCaptureField({ capture, existingUrl, emptyLabel, emptyHint, previewAlt = 'Photo', accent = 'amber' }) {
    const { preview, processing, inputRef, pick, clear } = capture;
    const accentEmpty = accent === 'blue'
        ? 'border-blue-300 bg-blue-50 active:bg-blue-100 text-blue-900'
        : 'border-amber-300 bg-amber-50 active:bg-amber-100 text-amber-900';
    const accentHint = accent === 'blue' ? 'text-blue-700' : 'text-amber-700';
    return (
        <div className="space-y-2">
            {preview ? (
                <div className="relative rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                    <img src={preview} alt={`${previewAlt} preview`} className="w-full max-h-48 object-cover" />
                    <div className="absolute top-2 right-2 flex gap-2">
                        <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>Replace</Button>
                        <Button variant="danger" size="sm" className="!bg-white" onClick={clear}>Remove</Button>
                    </div>
                </div>
            ) : existingUrl ? (
                <div className="relative rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
                    <img src={existingUrl} alt={`${previewAlt} on file`} className="w-full max-h-48 object-cover" />
                    <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded-md bg-emerald-600/90 text-white text-[10px] font-bold">ON FILE</span>
                    </div>
                    <div className="absolute top-2 right-2">
                        <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>Re-take</Button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={processing}
                    className={`w-full px-4 py-5 rounded-xl border-2 border-dashed text-center ${accentEmpty} disabled:opacity-50`}
                >
                    <div className="text-sm font-bold mb-0.5">{processing ? 'Processing…' : emptyLabel}</div>
                    {emptyHint && <div className={`text-[11px] ${accentHint}`}>{emptyHint}</div>}
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                capture="environment"
                className="hidden"
                onChange={e => pick(e.target.files?.[0])}
            />
        </div>
    );
}
